"use client";

import { Stars } from "@/components/common/feedback/feedback-stars";
import { formatImageUrl } from "@/lib/utils";
import { Building2Icon, UserIcon } from "lucide-react";
import Image from "next/image";
import { TProductReview } from "@/types/productv2";
import ProfilePicture from "@/components/common/profile-picture";

const FeaturedReviewCard = ({ review }: { review: TProductReview }) => {
	return (
		<div className="w-full sm:max-w-md md:max-w-xl lg:max-w-2xl">
			<div className="rounded-xl border border-gray-100 bg-blue-50 p-4 shadow-sm transition-shadow hover:shadow-md sm:p-6">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start">
					<div className="flex-shrink-0">
						{review?.profilePicture ? (
							<ProfilePicture
								isFounder={review?.isFounder}
								isVerified={review?.isVerified}
								profilePictureUrl={formatImageUrl(review?.profilePicture)}
								className={{
									container: "h-16 w-16 rounded-full p-0.5",
									image: "rounded-full object-cover text-gray-600",
									badgeImage: "-bottom-3.5 h-8 w-8",
								}}
							/>
						) : (
							<div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
								<UserIcon className="h-8 w-8 text-gray-500" />
							</div>
						)}
					</div>

					<div className="flex w-full flex-col justify-between gap-4 sm:flex-row">
						<div className="space-y-1">
							<h3 className="text-lg font-semibold text-gray-900">{review?.name || "Full Name"}</h3>
							<p className="text-sm font-medium text-gray-500">{review?.jobTitle || "Job Title"}</p>
						</div>

						<div className="flex flex-col items-center gap-2 sm:items-end">
							{review?.companyLogo ? (
								<div className="h-12 w-12 overflow-hidden rounded-md bg-gray-50 sm:h-14 sm:w-14">
									<Image
										width={480}
										height={480}
										src={formatImageUrl(review?.companyLogo)}
										alt={review?.companyName || "Company Name"}
										className="h-full w-full object-contain"
									/>
								</div>
							) : (
								<div className="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100">
									<Building2Icon className="h-6 w-6 text-gray-500" />
								</div>
							)}
							<p className="text-center text-xs font-medium text-gray-500">
								{review?.companyName || "Company Name"}
							</p>
						</div>
					</div>
				</div>

				<div className="mt-4 space-y-3">
					<div className="flex items-center">
						<Stars
							readOnly
							rating={review?.overallRating || 0}
							classNames={{ star: "w-6 h-6", starContainer: "w-7 h-7" }}
						/>
					</div>
					<h6 className="text-base font-semibold text-gray-900">
						{review?.title || "Review Title"}
					</h6>
					<p className="max-h-40 overflow-y-auto text-sm text-gray-600">
						{review?.summary || "Review Content"}
					</p>
				</div>
			</div>
		</div>
	);
};

export default FeaturedReviewCard;
